import React, { useEffect, useState } from "react";

import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

import Chart from "./Chart";
import ForumIcon from "@material-ui/icons/Forum";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import SendIcon from "@material-ui/icons/Send";
import TodayIcon from "@material-ui/icons/Today";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import NotificationImportantIcon from "@material-ui/icons/NotificationImportant";
import WarningIcon from "@material-ui/icons/Warning";

const useStyles = makeStyles(theme => ({
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	cardsGrid: {
		marginBottom: theme.spacing(1),
	},
	fixedHeightPaper: {
		padding: theme.spacing(3),
		display: "flex",
		overflow: "hidden",
		flexDirection: "column",
		height: 340,
	},
	customFixedHeightPaper: {
		padding: theme.spacing(3),
		display: "flex",
		overflow: "hidden",
		flexDirection: "column",
		height: 150,
		justifyContent: "center",
	},
	customFixedHeightPaperCompact: {
		justifyContent: "flex-start",
		paddingTop: theme.spacing(2.5),
	},
	cardHeader: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: theme.spacing(2),
	},
	cardHeaderCompact: {
		marginBottom: theme.spacing(1),
	},
	cardTitle: {
		fontWeight: 600,
		fontSize: "0.95rem",
		color: theme.palette.text.secondary,
		textTransform: "uppercase",
		letterSpacing: "0.5px",
	},
	cardValue: {
		fontWeight: 800,
		fontSize: "2.5rem",
		color: theme.palette.text.primary,
		lineHeight: 1,
	},
	cardValueCompact: {
		fontWeight: 700,
		fontSize: "1.2rem",
		color: theme.palette.text.primary,
		lineHeight: 1.25,
		overflow: "hidden",
		textOverflow: "clip",
		whiteSpace: "nowrap",
		textTransform: "none",
	},
	cardSubtitle: {
		marginTop: theme.spacing(0.5),
		fontSize: "0.8rem",
		color: theme.palette.text.secondary,
	},
	iconWrapper: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: 48,
		height: 48,
		borderRadius: "50%",
	},
	cardItem: {
		display: "flex",
	},
	cardPaper: {
		width: "100%",
	},
}));

const initialMetrics = {
	openTickets: 0,
	pendingTickets: 0,
	myOpenTickets: 0,
	unreadTickets: 0,
	staleTickets24h: 0,
	ticketsToday: 0,
	closedToday: 0,
	messagesSentToday: 0,
	resolutionRateLast30Days: 0,
};

const Dashboard = () => {
	const classes = useStyles();
	const [loading, setLoading] = useState(true);
	const [metrics, setMetrics] = useState(initialMetrics);

	useEffect(() => {
		let isMounted = true;

		const loadDashboardMetrics = async () => {
			try {
				const { data } = await api.get("/dashboard");

				if (!isMounted) {
					return;
				}

				setMetrics({
					...initialMetrics,
					...data?.counters,
				});
			} catch (err) {
				if (isMounted) {
					toastError(err);
				}
			} finally {
				if (isMounted) {
					setLoading(false);
				}
			}
		};

		loadDashboardMetrics();

		return () => {
			isMounted = false;
		};
	}, []);

	const metricValue = value => (loading ? "-" : value);

	const baseCards = [
		{
			title: i18n.t("dashboard.messages.inAttendance.title"),
			value: metricValue(metrics.openTickets),
			icon: <ForumIcon />,
			iconStyle: { backgroundColor: "rgba(79, 70, 229, 0.1)", color: "#4F46E5" },
		},
		{
			title: i18n.t("dashboard.messages.waiting.title"),
			value: metricValue(metrics.pendingTickets),
			icon: <AccessTimeIcon />,
			iconStyle: { backgroundColor: "rgba(245, 158, 11, 0.1)", color: "#F59E0B" },
		},
		{
			title: i18n.t("dashboard.metrics.myOpenTickets"),
			value: metricValue(metrics.myOpenTickets),
			icon: <AssignmentIndIcon />,
			iconStyle: { backgroundColor: "rgba(34, 197, 94, 0.1)", color: "#22C55E" },
		},
		{
			title: i18n.t("dashboard.metrics.unreadTickets"),
			value: metricValue(metrics.unreadTickets),
			icon: <NotificationImportantIcon />,
			iconStyle: { backgroundColor: "rgba(220, 38, 38, 0.1)", color: "#DC2626" },
		},
	];

	const extraCards = [
		{
			title: i18n.t("dashboard.metrics.staleTickets24h"),
			value: metricValue(metrics.staleTickets24h),
			subtitle: i18n.t("dashboard.metrics.staleTickets24hHint"),
			icon: <WarningIcon />,
			iconStyle: { backgroundColor: "rgba(234, 88, 12, 0.1)", color: "#EA580C" },
			compact: true,
		},
		{
			title: i18n.t("dashboard.metrics.ticketsToday"),
			value: metricValue(metrics.ticketsToday),
			icon: <TodayIcon />,
			iconStyle: { backgroundColor: "rgba(59, 130, 246, 0.1)", color: "#3B82F6" },
		},
		{
			title: i18n.t("dashboard.metrics.closedToday"),
			value: metricValue(metrics.closedToday),
			icon: <CheckCircleOutlineIcon />,
			iconStyle: { backgroundColor: "rgba(16, 185, 129, 0.1)", color: "#10B981" },
		},
		{
			title: i18n.t("dashboard.metrics.messagesSentToday"),
			value: metricValue(metrics.messagesSentToday),
			icon: <SendIcon />,
			iconStyle: { backgroundColor: "rgba(99, 102, 241, 0.1)", color: "#6366F1" },
		},
		{
			title: i18n.t("dashboard.metrics.resolutionRateLast30Days"),
			value: loading ? "-" : `${metrics.resolutionRateLast30Days}%`,
			icon: <DoneAllIcon />,
			iconStyle: { backgroundColor: "rgba(34, 197, 94, 0.1)", color: "#22C55E" },
			compact: true,
		},
	];

	const renderMetricCard = (card, gridProps = { xs: 12, sm: 6, md: 4 }) => (
		<Grid item key={card.title} className={classes.cardItem} {...gridProps}>
			<Paper
				className={`${classes.cardPaper} ${classes.customFixedHeightPaper} ${
					card.compact ? classes.customFixedHeightPaperCompact : ""
				}`}
			>
				<div
					className={`${classes.cardHeader} ${
						card.compact ? classes.cardHeaderCompact : ""
					}`}
				>
					<Typography component="h3" className={classes.cardTitle}>
						{card.title}
					</Typography>
					<div className={classes.iconWrapper} style={card.iconStyle}>
						{card.icon}
					</div>
				</div>
				<Typography
					component="h1"
					className={card.compact ? classes.cardValueCompact : classes.cardValue}
				>
					{card.value}
				</Typography>
				{card.subtitle && (
					<Typography component="p" className={classes.cardSubtitle}>
						{card.subtitle}
					</Typography>
				)}
			</Paper>
		</Grid>
	);

	return (
		<div>
			<Container maxWidth="lg" className={classes.container}>
				<Grid container spacing={3} className={classes.cardsGrid}>
					{baseCards.map(card => renderMetricCard(card, { xs: 12, sm: 6, md: 4 }))}
					{extraCards.map(card => renderMetricCard(card, { xs: 12, sm: 6, md: 4 }))}
				</Grid>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<Paper className={classes.fixedHeightPaper}>
							<Chart />
						</Paper>
					</Grid>
				</Grid>
			</Container>
		</div>
	);
};

export default Dashboard;